import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Play, Trophy, User, Zap, Volume2, VolumeX, Loader2, Check, X, Flame, Award } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const TIMER = 10;

/** FastAPI can return a structured 422 detail (list of objects); Sonner
 *  crashes when React tries to render an object as a child. Normalize
 *  everything to a plain string. */
function humanErr(e, fallback = "Something went wrong") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return e?.message || fallback;
}

/** Persistent AudioContext — mobile Safari/Chrome require a user gesture to
 *  unlock. Reusing a single context is far more reliable than spinning up
 *  a new one per beep. */
let _actx = null;
function _getCtx() {
  try {
    if (!_actx) _actx = new (window.AudioContext || window.webkitAudioContext)();
    if (_actx.state === "suspended") _actx.resume().catch(() => {});
    return _actx;
  } catch { return null; }
}
/** Unlock the audio context via a user gesture (tap Play, tap a choice).
 *  Some mobile browsers ignore any sound played without a fresh gesture,
 *  so a silent tick right after the tap gets us permission. */
function unlockAudio() {
  const ctx = _getCtx();
  if (!ctx) return;
  try {
    const o = ctx.createOscillator(); const g = ctx.createGain();
    g.gain.value = 0.0001; o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.02);
  } catch {}
}
/** Simple sine beep with a short envelope so it doesn't click. */
function tone(freq = 800, ms = 120, vol = 0.18) {
  const ctx = _getCtx();
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine"; o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(vol, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, now + ms / 1000);
    o.connect(g); g.connect(ctx.destination);
    o.start(now); o.stop(now + ms / 1000 + 0.02);
  } catch {}
}
/** A short 2-note "correct" chime (C→G rising). */
function playCorrect() { tone(880, 110, 0.18); setTimeout(() => tone(1320, 160, 0.18), 90); }
/** Descending "wrong" buzz. */
function playWrong()   { tone(220, 180, 0.18); setTimeout(() => tone(140, 220, 0.18), 130); }
/** Slow tick for 5-4 seconds remaining. */
function playTick()    { tone(700, 60, 0.14); }
/** Urgent tick for the final 3 seconds. */
function playUrgent()  { tone(1400, 80, 0.22); }
/** Game start ta-da. */
function playStart()   { tone(660, 90, 0.15); setTimeout(() => tone(880, 90, 0.15), 90); setTimeout(() => tone(1200, 140, 0.18), 190); }

/** Legacy alias — kept only so any external caller doesn't crash. New code
 *  should use tone() / playCorrect() / playWrong() / playTick() / playUrgent(). */
function beep(freq = 800, duration = 100, vol = 0.15) { tone(freq, duration, vol); }

export default function TriviaApp() {
  const { user, loading } = useAuth();
  const [view, setView] = useState("home");   // home | play | leaderboard | profile
  const [muted, setMuted] = useState(false);
  useEffect(() => { document.title = "Jixxeo Trivia"; }, []);

  return (
    <div data-testid="trivia-app" className="min-h-screen bg-gradient-to-b from-[#0F0326] via-[#1B0538] to-[#0A0620] text-[#F5F0FF]" style={{ fontFamily: "'Cabinet Grotesk','Outfit',ui-sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0F0326]/85 border-b border-[#FF00E5]/25">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/browse" className="p-2 -ml-2 rounded-full hover:bg-white/10" data-testid="trivia-back"><ArrowLeft className="w-4 h-4" /></Link>
          <div className="min-w-0 flex-1">
            <div className="font-black text-lg leading-none">Jixxeo <span className="text-[#FF00E5]">Trivia</span></div>
            <div className="text-[9px] uppercase tracking-widest text-white/40">10 questions · 10 seconds each</div>
          </div>
          <button data-testid="trivia-mute" onClick={() => setMuted((m) => !m)}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 pb-24">
        {loading ? <div className="py-16 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          : !user ? <SignInPrompt />
          : view === "play" ? <GameView muted={muted} onExit={() => setView("home")} />
          : view === "leaderboard" ? <Leaderboard />
          : view === "profile" ? <Profile />
          : <Home onPlay={() => setView("play")} />}
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-[#0F0326]/90 border-t border-[#FF00E5]/25">
        <div className="max-w-2xl mx-auto grid grid-cols-4">
          <Tab active={view === "home"} onClick={() => setView("home")} icon={<Play className="w-5 h-5" />} label="Home" testId="trivia-tab-home" />
          <Tab active={view === "play"} onClick={() => setView("play")} icon={<Zap className="w-5 h-5" />} label="Play" testId="trivia-tab-play" />
          <Tab active={view === "leaderboard"} onClick={() => setView("leaderboard")} icon={<Trophy className="w-5 h-5" />} label="Board" testId="trivia-tab-board" />
          <Tab active={view === "profile"} onClick={() => setView("profile")} icon={<User className="w-5 h-5" />} label="Me" testId="trivia-tab-me" />
        </div>
      </nav>
    </div>
  );
}

function SignInPrompt() {
  return <div className="py-16 text-center">
    <div className="font-black text-2xl mb-2">Sign in to play</div>
    <Link to="/login?next=/trivia" className="inline-flex items-center gap-1 bg-[#FF00E5] text-white font-black text-xs uppercase tracking-widest px-5 py-2.5 rounded-full mt-3">Sign in</Link>
  </div>;
}

function Tab({ active, onClick, icon, label, testId }) {
  return <button data-testid={testId} onClick={onClick}
    className={"flex flex-col items-center gap-0.5 py-2.5 text-[10px] uppercase tracking-widest transition-colors " +
      (active ? "text-[#FF00E5]" : "text-white/50 hover:text-white/80")}>
    {icon}<span className="font-bold">{label}</span>
  </button>;
}

function Home({ onPlay }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/trivia/me/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const handlePlay = () => { unlockAudio(); playStart(); onPlay(); };
  return (
    <div className="space-y-5" data-testid="trivia-home">
      <section className="rounded-3xl border-2 border-[#FF00E5]/40 bg-gradient-to-br from-[#FF00E5]/25 via-[#7A00FF]/15 to-transparent p-6 text-center">
        <div className="text-[10px] uppercase tracking-widest text-[#FF00E5] font-bold">Live now</div>
        <div className="font-black text-4xl mt-1">Play Jixxeo Trivia</div>
        <div className="text-sm text-white/70 mt-2">10 questions · 10s each · earn Jixxeo Points and streak bonuses.</div>
        <button data-testid="home-play-btn" onClick={handlePlay}
          className="mt-5 inline-flex items-center gap-1.5 bg-[#FF00E5] text-white font-black text-sm uppercase tracking-widest px-6 py-3 rounded-full shadow-[0_0_25px_rgba(255,0,229,0.5)]">
          <Play className="w-4 h-4" fill="currentColor" /> Play now
        </button>
      </section>
      {stats && (
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Today" value={stats.today.points} />
          <Stat label="This week" value={stats.week.points} />
          <Stat label="All-time" value={stats.alltime.points} />
        </div>
      )}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-3 text-xs text-white/60">
        <b className="text-white/80">Rewards Rules:</b> Earn Jixxeo Points by playing legitimately. No incentivized ad clicks. Payouts require reaching threshold in the <Link to="/terms" className="underline">Terms</Link>.
      </div>
    </div>
  );
}
function Stat({ label, value }) { return <div className="rounded-2xl bg-white/5 border border-white/10 p-3 text-center"><div className="text-[9px] uppercase tracking-widest text-white/50">{label}</div><div className="font-black text-xl mt-1">{value.toLocaleString()}</div></div>; }

function GameView({ muted, onExit }) {
  const [game, setGame] = useState(null);
  const [idx, setIdx] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TIMER);
  const [picked, setPicked] = useState(null);
  const [reveal, setReveal] = useState(null);   // {correct_idx, correct, points}
  const [done, setDone] = useState(null);       // final result
  const [busy, setBusy] = useState(false);
  const startTs = useRef(0);
  const tickRef = useRef(null);

  useEffect(() => {
    api.post("/trivia/game/start").then((r) => { setGame(r.data); startTs.current = Date.now(); })
      .catch((e) => { toast.error(humanErr(e, "Couldn't start game")); onExit(); });
    return () => clearInterval(tickRef.current);
  }, []);

  useEffect(() => {
    if (!game || reveal || done) return;
    startTs.current = Date.now();
    setTimeLeft(TIMER);
    clearInterval(tickRef.current);
    let lastSec = TIMER;
    tickRef.current = setInterval(() => {
      const remaining = TIMER - Math.floor((Date.now() - startTs.current) / 1000);
      if (remaining <= 0) { clearInterval(tickRef.current); submit(-1); return; }
      if (remaining !== lastSec) {
        setTimeLeft(remaining);
        // Fire tick sounds once per whole second — urgent last 3, softer at 5-4s
        if (!muted) {
          if (remaining <= 3) playUrgent();
          else if (remaining <= 5) playTick();
        }
        lastSec = remaining;
      }
    }, 100);
    return () => clearInterval(tickRef.current);
  // eslint-disable-next-line
  }, [game, idx, reveal, done]);

  const submit = async (choiceIdx) => {
    if (picked !== null || busy) return;
    // Any tap unlocks audio if we didn't get it on the Play button.
    unlockAudio();
    setPicked(choiceIdx);
    setBusy(true);
    clearInterval(tickRef.current);
    const q = game.questions[idx];
    const timeMs = Date.now() - startTs.current;
    const idxToSend = (choiceIdx === null || choiceIdx === undefined) ? -1 : choiceIdx;
    try {
      const r = await api.post(`/trivia/game/${game.game_id}/answer`, {
        question_id: q.id, choice_idx: idxToSend, time_ms: Math.min(timeMs, TIMER * 1000),
      });
      setReveal(r.data);
      if (!muted) { r.data.correct ? playCorrect() : playWrong(); }
      setTimeout(() => next(r.data), 1800);
    } catch (e) { toast.error(humanErr(e, "Answer failed")); setBusy(false); }
  };

  const next = async (last) => {
    setPicked(null); setReveal(null); setBusy(false);
    if (idx + 1 >= game.questions.length) {
      try {
        const r = await api.post(`/trivia/game/${game.game_id}/finish`);
        // Prefer server's authoritative best_streak so an earlier long
        // streak that later broke is still surfaced correctly.
        setDone({
          points: r.data.points ?? 0,
          correct: r.data.correct ?? 0,
          total: r.data.total ?? game.questions.length,
          best_streak: r.data.best_streak ?? last?.streak ?? 0,
        });
      } catch (e) { toast.error(humanErr(e, "Couldn't save score")); }
      return;
    }
    setIdx(idx + 1);
  };

  if (!game) return <div className="py-16 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /> Loading game…</div>;

  if (done) return <GameOver done={done} onExit={onExit} />;
  const q = game.questions[idx];
  return (
    <div className="space-y-4" data-testid="trivia-play">
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-widest text-white/50">Question {idx + 1} / {game.questions.length}</div>
        <div className={"font-black text-7xl tabular-nums mt-2 " + (timeLeft <= 3 ? "text-[#F87171] animate-pulse" : "text-[#FF00E5]")}
          data-testid="trivia-timer">{String(timeLeft).padStart(2, "0")}</div>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-white/40 text-center">{q.category} · {q.difficulty}</div>
      <div className="rounded-3xl bg-black/40 border border-white/10 p-5 text-center font-black text-xl min-h-[8rem] flex items-center justify-center" data-testid="trivia-question">
        {q.text}
      </div>
      <div className="grid grid-cols-1 gap-2" data-testid="trivia-choices">
        {q.choices.map((c, i) => {
          let cls = "border-white/10 bg-white/5 hover:border-[#FF00E5]/40";
          if (reveal) {
            if (i === reveal.correct_idx) cls = "border-[#39FF14] bg-[#39FF14]/15";
            else if (i === picked) cls = "border-[#F87171] bg-[#F87171]/15";
            else cls = "border-white/5 bg-white/[.02] opacity-50";
          } else if (picked === i) cls = "border-[#FF00E5] bg-[#FF00E5]/15";
          return (
            <button key={i} data-testid={`trivia-choice-${i}`} onClick={() => submit(i)} disabled={picked !== null || busy}
              className={"rounded-2xl border p-4 text-left font-black text-lg transition-colors flex items-center gap-2 " + cls}>
              <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center font-black text-sm">{String.fromCharCode(65 + i)}</div>
              <span className="flex-1">{c}</span>
              {reveal && i === reveal.correct_idx && <Check className="w-5 h-5 text-[#39FF14]" />}
              {reveal && i === picked && !reveal.correct && <X className="w-5 h-5 text-[#F87171]" />}
            </button>
          );
        })}
      </div>
      {reveal && (
        <div className="text-center" data-testid="trivia-reveal">
          <div className={"font-black text-2xl " + (reveal.correct ? "text-[#39FF14]" : "text-[#F87171]")}>
            {reveal.correct ? `+${reveal.points_awarded} points` : (reveal.timed_out ? "Time's up!" : "Wrong answer")}
          </div>
          {reveal.streak > 1 && reveal.correct && <div className="text-sm text-[#FFD700] mt-1"><Flame className="w-4 h-4 inline" /> {reveal.streak}× streak!</div>}
        </div>
      )}
    </div>
  );
}

function GameOver({ done, onExit }) {
  return (
    <div className="text-center space-y-4 py-8" data-testid="trivia-gameover">
      <Award className="w-16 h-16 text-[#FFD700] mx-auto" />
      <div className="font-black text-4xl">Game complete!</div>
      <div className="rounded-3xl border-2 border-[#FF00E5]/40 bg-[#FF00E5]/10 p-6">
        <div className="text-[10px] uppercase tracking-widest text-white/50">Final points</div>
        <div className="font-black text-6xl text-[#FF00E5] tabular-nums">{done.points.toLocaleString()}</div>
        <div className="text-sm text-white/70 mt-2">{done.correct} / {done.total} correct · best streak {done.best_streak}</div>
      </div>
      <button data-testid="gameover-again" onClick={onExit} className="bg-[#FF00E5] text-white font-black text-sm uppercase tracking-widest px-6 py-3 rounded-full">Play again</button>
    </div>
  );
}

function Leaderboard() {
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/trivia/leaderboard?period=${period}`).then((r) => setData(r.data)); }, [period]);
  return (
    <div className="space-y-4" data-testid="trivia-leaderboard">
      <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1">
        {["today","week","alltime"].map((p) => (
          <button key={p} data-testid={`board-period-${p}`} onClick={() => setPeriod(p)}
            className={"flex-1 py-2 text-xs uppercase tracking-widest font-black rounded-full " + (period === p ? "bg-[#FF00E5] text-white" : "text-white/60")}>{p}</button>
        ))}
      </div>
      {!data ? <div className="py-8 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> :
        data.leaderboard.length === 0 ? <div className="py-12 text-center text-white/50">No games played yet — be the first!</div> :
        <ul className="space-y-1">
          {data.leaderboard.map((r) => (
            <li key={r.user_id} className={"flex items-center gap-3 rounded-2xl p-3 border " + (data.me?.user_id === r.user_id ? "border-[#FF00E5]/50 bg-[#FF00E5]/10" : "border-white/10 bg-white/5")}>
              <div className={"w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm " + (r.rank <= 3 ? "bg-[#FFD700] text-black" : "bg-white/10")}>{r.rank}</div>
              <div className="w-8 h-8 rounded-full shrink-0" style={{ background: r.avatar_color }} />
              <div className="min-w-0 flex-1">
                <div className="font-black text-sm truncate">{r.user_name}</div>
                <div className="text-[10px] text-white/50">{r.games} games · {r.best_streak}× best streak</div>
              </div>
              <div className="font-black text-lg tabular-nums text-[#FF00E5]">{r.points.toLocaleString()}</div>
            </li>
          ))}
        </ul>}
    </div>
  );
}

function Profile() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/trivia/me/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="py-16 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  return (
    <div className="space-y-4" data-testid="trivia-profile">
      <div className="rounded-3xl border border-[#FF00E5]/30 bg-gradient-to-br from-[#FF00E5]/15 to-black/40 p-5 text-center">
        <div className="text-[10px] uppercase tracking-widest text-white/50">All-time points</div>
        <div className="font-black text-5xl text-[#FF00E5]">{stats.alltime.points.toLocaleString()}</div>
        <div className="text-sm text-white/70 mt-1">{stats.alltime.games} games · {stats.alltime.correct} correct · {stats.alltime.best_streak}× best streak</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center">
          <div className="text-[10px] uppercase tracking-widest text-white/50">Today</div>
          <div className="font-black text-2xl">{stats.today.points.toLocaleString()}</div>
          <div className="text-[10px] text-white/50 mt-1">{stats.today.games} games</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center">
          <div className="text-[10px] uppercase tracking-widest text-white/50">This week</div>
          <div className="font-black text-2xl">{stats.week.points.toLocaleString()}</div>
          <div className="text-[10px] text-white/50 mt-1">{stats.week.games} games</div>
        </div>
      </div>
      <div className="rounded-2xl border border-[#FFD700]/30 bg-[#FFD700]/5 p-4">
        <div className="font-black text-sm mb-1 text-[#FFD700]">Rewards Rules</div>
        <ul className="text-xs text-white/70 space-y-1 list-disc pl-5">
          <li>Earn Jixxeo Points ONLY by legitimate gameplay.</li>
          <li>Points are non-transferable and non-refundable virtual currency.</li>
          <li>No incentivized ad clicks. Ads never disguised as game elements.</li>
          <li>Detected abuse voids all points and may result in account suspension.</li>
          <li>See full <Link to="/terms" className="underline">Terms of Service</Link>.</li>
        </ul>
      </div>
    </div>
  );
}
